/**
 * Accessory Configurator — buy-box for accessory product pages.
 * Variant selection, dynamic price, quantity, add-to-cart with
 * cart header auto-update and dynamic button text
 * (mirrors wheel-configurator behaviour).
 */

class AccessoryConfiguratorElement extends HTMLElement {
  connectedCallback() {
    // Parse product JSON
    const jsonEl = this.querySelector('[data-role="ac-product-json"]');
    if (!jsonEl) return;
    try {
      const data = JSON.parse(jsonEl.textContent);
      this._variants = data.variants || [];
      this._options = data.options || [];
    } catch (e) {
      console.error('AC: Failed to parse product JSON', e);
      return;
    }

    this._selectedVariant = null;
    this._selectedOptions = {};
    this._isOrderProduct = this.hasAttribute('data-order-product');

    // DOM refs
    this._variantsEl = this.querySelector('[data-role="ac-variants"]');
    this._priceEl = this.querySelector('[data-role="ac-price"]');
    this._qtyInput = this.querySelector('[data-role="ac-qty"]');
    this._addBtn = this.querySelector('[data-role="ac-add"]');
    this._stockEl = this.querySelector('[data-role="ac-stock"]');
    this._feedbackEl = this.querySelector('[data-role="ac-feedback"]');

    // Hide native variant picker if present
    const nativePicker = document.querySelector('variant-picker');
    if (nativePicker) nativePicker.style.display = 'none';

    // Render variant options (dropdowns)
    this._renderOptions();

    // Qty +/- buttons
    this.querySelector('[data-role="ac-qty-minus"]')?.addEventListener('click', () => {
      const val = parseInt(this._qtyInput.value, 10) || 1;
      this._qtyInput.value = Math.max(1, val - 1);
      this._updatePrice(this._selectedVariant);
    });
    this.querySelector('[data-role="ac-qty-plus"]')?.addEventListener('click', () => {
      const val = parseInt(this._qtyInput.value, 10) || 1;
      this._qtyInput.value = Math.min(99, val + 1);
      this._updatePrice(this._selectedVariant);
    });

    // Add to cart
    this._addBtn?.addEventListener('click', () => this._addToCart());

    // Select first variant
    if (this._variants.length > 0) {
      this._selectVariant(this._variants[0]);
    }
  }

  // ── Variant option rendering ──────────────────────────────────────

  _renderOptions() {
    if (!this._variantsEl) return;
    if (this._options.length === 0 || (this._options.length === 1 && this._variants.length <= 1)) {
      return;
    }

    this._variantsEl.innerHTML = this._options.map((opt, idx) => {
      const values = opt.values || [];
      return `
        <div class="ac__option-group">
          <div class="ac__option-label">${this._esc(opt.name)}</div>
          <select class="ac__option-select" data-option-index="${idx}">
            ${values.map(v => `<option value="${this._esc(v)}">${this._esc(v)}</option>`).join('')}
          </select>
        </div>
      `;
    }).join('');

    for (const select of this._variantsEl.querySelectorAll('select')) {
      select.addEventListener('change', () => this._onOptionChange());
    }
  }

  _onOptionChange() {
    const selects = this._variantsEl.querySelectorAll('select');
    const selectedValues = [...selects].map(s => s.value);

    const variant = this._variants.find(v =>
      v.options && v.options.every((opt, i) => opt === selectedValues[i])
    );

    if (variant) {
      this._selectVariant(variant);
    } else {
      this._selectedVariant = null;
      this._updateAddButton();
    }
  }

  // ── Select variant ────────────────────────────────────────────────

  _selectVariant(variant) {
    this._selectedVariant = variant;
    this._updatePrice(variant);
    this._updateStock(variant);
    this._updateAddButton();
    this._updateUrl(variant);

    // Dispatch variant change for compatibility banner etc
    document.dispatchEvent(new CustomEvent('variant:update', { detail: { variant } }));
  }

  // ── Price ─────────────────────────────────────────────────────────

  _updatePrice(variant) {
    if (!this._priceEl || !variant) return;

    const qty = parseInt(this._qtyInput?.value, 10) || 1;
    const total = variant.price * qty;
    const price = this._formatMoney(total);
    const qtyLabel = qty > 1 ? ` (${qty} stk)` : '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const compare = this._formatMoney(variant.compare_at_price * qty);
      this._priceEl.innerHTML = `<span class="ac__price-compare">${compare}</span>${price}${qtyLabel}`;
    } else {
      this._priceEl.textContent = `${price}${qtyLabel}`;
    }
  }

  _formatMoney(cents) {
    return (cents / 100).toLocaleString('nb-NO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }) + ' kr';
  }

  // ── Stock ─────────────────────────────────────────────────────────

  _updateStock(variant) {
    if (!this._stockEl) return;
    if (!variant) {
      this._stockEl.textContent = '';
      this._stockEl.className = 'ac__stock';
      return;
    }

    if (this._isOrderProduct) {
      if (variant.available) {
        if (window.location.pathname.includes('/collections/tilbehor')) {
  this._stockEl.textContent = 'På lager · 10–14 dager';
  this._stockEl.className = 'ac__stock ac__stock--in';
} else {
  this._stockEl.textContent = 'Bestillingsvare';
  this._stockEl.className = 'ac__stock ac__stock--order';
}
        this._stockEl.className = 'ac__stock ac__stock--order';
      } else {
        this._stockEl.textContent = 'Utsolgt';
        this._stockEl.className = 'ac__stock ac__stock--out';
      }
      return;
    }

    if (variant.available) {
      if (variant.inventory_quantity > 0) {
        this._stockEl.textContent = `${variant.inventory_quantity} på lager`;
        this._stockEl.className = 'ac__stock ac__stock--in';
      } else {
        this._stockEl.textContent = 'På lager';
        this._stockEl.className = 'ac__stock ac__stock--in';
      }
    } else {
      this._stockEl.textContent = 'Utsolgt';
      this._stockEl.className = 'ac__stock ac__stock--out';
    }
  }

  // ── Add button (dynamic text) ─────────────────────────────────────

  _updateAddButton() {
    if (!this._addBtn) return;
    const btnTop = this._addBtn.querySelector('.ac__add-btn-top');
    const v = this._selectedVariant;

    if (!v) {
      this._addBtn.disabled = true;
      if (btnTop) btnTop.textContent = 'Utilgjengelig';
    } else if (!v.available) {
      this._addBtn.disabled = true;
      if (btnTop) btnTop.textContent = 'Utsolgt';
    } else {
      this._addBtn.disabled = false;
      if (btnTop) btnTop.textContent = 'Legg i handlekurv';
    }
  }

  // ── URL ───────────────────────────────────────────────────────────

  _updateUrl(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  // ── Add to cart ───────────────────────────────────────────────────

  async _addToCart() {
    const variant = this._selectedVariant;
    if (!variant || !variant.available) return;

    const qty = parseInt(this._qtyInput?.value, 10) || 1;
    const btnTop = this._addBtn.querySelector('.ac__add-btn-top');

    // Disable + loading text
    this._addBtn.disabled = true;
    if (btnTop) btnTop.textContent = 'Legger til...';
    this._hideFeedback();

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: qty }],
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Kunne ikke legge til i handlekurv');
      }

      // Success — dynamic button text
      if (btnTop) btnTop.textContent = `Lagt til ${qty}x \u2713`;
      this._showFeedback('success', `${qty}x lagt til i handlekurven`);

      // Refresh cart header
      this._refreshCart();

      // Reset button after delay
      setTimeout(() => this._updateAddButton(), 2500);

    } catch (e) {
      console.error('AC: Add to cart failed', e);
      this._addBtn.disabled = false;
      if (btnTop) btnTop.textContent = 'Legg i handlekurv';
      this._showFeedback('error', e.message);
    }
  }

  // ── Cart refresh (mirrors wheel-configurator) ─────────────────────

  async _refreshCart() {
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();

      // Update all common cart count selectors used by Shopify themes
      const countSelectors = [
        '[data-cart-count]',
        '.cart-count',
        '.cart-count-bubble span',
        '.cart-icon-bubble span',
        '#cart-icon-bubble span',
        '#CartCount',
        '.js-cart-count',
        '.header__cart-count',
        'cart-count',
        '[data-cart-item-count]',
      ];
      const count = cart.item_count || 0;
      for (const sel of countSelectors) {
        document.querySelectorAll(sel).forEach(el => {
          el.textContent = count;
          const bubble = el.closest('[data-cart-count], .cart-count-bubble, .cart-icon-bubble, #cart-icon-bubble');
          if (bubble) bubble.hidden = count === 0;
        });
      }

      // Dispatch common cart events
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } }));
      window.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } }));

      // Section Rendering API — re-render cart/header sections
      const sectionIds = this._getCartSectionIds();
      if (sectionIds.length > 0) {
        fetch(`/?sections=${sectionIds.join(',')}`)
          .then(r => r.json())
          .then(sections => {
            for (const [id, html] of Object.entries(sections)) {
              const existing = document.getElementById(`shopify-section-${id}`);
              if (existing) {
                const temp = document.createElement('div');
                temp.innerHTML = html;
                const newSection = temp.querySelector(`#shopify-section-${id}`);
                if (newSection) existing.innerHTML = newSection.innerHTML;
              }
            }
          })
          .catch(() => {});
      }
    } catch (e) {
      console.warn('AC: cart refresh failed', e);
    }
  }

  _getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('[id^="shopify-section-"]').forEach(el => {
      const id = el.id.replace('shopify-section-', '');
      if (id.includes('cart') || id.includes('header')) {
        ids.push(id);
      }
    });
    return ids;
  }

  // ── Feedback ──────────────────────────────────────────────────────

  _showFeedback(type, msg) {
    if (!this._feedbackEl) return;
    this._feedbackEl.textContent = msg;
    this._feedbackEl.className = `ac__feedback ac__feedback--${type}`;
    this._feedbackEl.hidden = false;
    clearTimeout(this._feedbackTimeout);
    this._feedbackTimeout = setTimeout(() => this._hideFeedback(), 4000);
  }

  _hideFeedback() {
    if (!this._feedbackEl) return;
    this._feedbackEl.hidden = true;
  }

  // ── Utilities ─────────────────────────────────────────────────────

  _esc(str) {
    const d = document.createElement('div');
    d.textContent = String(str);
    return d.innerHTML;
  }
}

if (!customElements.get('accessory-configurator')) {
  customElements.define('accessory-configurator', AccessoryConfiguratorElement);
}
